import { useState } from 'react'
import { Card, Table, Typography, Tag } from 'antd'
import type { TablePaginationConfig } from 'antd'
import { useJobRuns } from '@/modules/dashboard/queries'
import type { JobRunItem } from '@/types/dashboard'
import { formatDateTime } from '@/utils/datetime'

const { Text } = Typography

const STATUS_COLOR: Record<string, string> = {
  success: 'green',
  partial: 'orange',
  failure: 'red',
  running: 'blue',
}

const STATUS_LABEL: Record<string, string> = {
  success: '成功',
  partial: '部分成功',
  failure: '失败',
  running: '运行中',
}

export default function JobRunsPage() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  const { data, isLoading } = useJobRuns({ page, page_size: pageSize })

  const handleTableChange = (pagination: TablePaginationConfig) => {
    const nextPageSize = pagination.pageSize || pageSize
    if (nextPageSize !== pageSize) {
      setPageSize(nextPageSize)
      setPage(1)
      return
    }
    if (pagination.current) setPage(pagination.current)
  }

  return (
    <Card title="任务运行记录">
      <Table<JobRunItem>
        size="small"
        rowKey="id"
        loading={isLoading}
        dataSource={data?.items || []}
        onChange={handleTableChange}
        pagination={{
          current: page,
          pageSize,
          total: data?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (total) => `共 ${total} 条`,
        }}
        columns={[
          {
            title: 'Run ID',
            dataIndex: 'run_id',
            width: 200,
            ellipsis: true,
          },
          {
            title: '任务码',
            dataIndex: 'job_code',
            width: 180,
          },
          {
            title: '状态',
            dataIndex: 'status',
            width: 100,
            render: (v: string) => (
              <Tag color={STATUS_COLOR[v] || 'default'}>{STATUS_LABEL[v] || v}</Tag>
            ),
          },
          {
            title: '开始时间',
            dataIndex: 'started_at',
            width: 170,
            render: (v: string) => formatDateTime(v),
          },
          {
            title: '结束时间',
            dataIndex: 'finished_at',
            width: 170,
            render: (v: string | null) => formatDateTime(v),
          },
          {
            title: '耗时(ms)',
            dataIndex: 'duration_ms',
            width: 100,
            render: (v: number | null) => (v != null ? v.toLocaleString() : '-'),
          },
          {
            title: '候选/成功/失败/警告',
            width: 160,
            render: (_: unknown, r: JobRunItem) => (
              <span>
                {r.candidate_count}/{r.success_count}/{r.failure_count}/{r.warning_count}
              </span>
            ),
          },
          {
            title: '摘要',
            dataIndex: 'result_summary',
            ellipsis: true,
            width: 250,
            render: (v: string | null) => v || '-',
          },
          {
            title: '错误信息',
            dataIndex: 'error_message',
            ellipsis: true,
            width: 200,
            render: (v: string | null) =>
              v ? <Text type="danger" ellipsis style={{ maxWidth: 180 }}>{v}</Text> : '-',
          },
        ]}
      />
    </Card>
  )
}
